import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Users, Calendar, CreditCard, Star, Plus, Check, X, Lock, Bell, Ticket, Award, Mail, AlertTriangle, Download, Loader2, QrCode, Crown } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useTranslations } from '../context/TranslationsContext';
import { useFitnet } from '../contexts/FitnetContext';
import PhonePrefixSelect from '../components/PhonePrefixSelect';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import Logo from '../components/Logo';
import { toast } from 'sonner';
import FlagIcon from '../components/FlagIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to calculate age from birth date
const calculateAge = (birthDate) => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// Minor Alert Component
const MinorAlert = ({ t }) => (
  <motion.div 
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className="mt-3 p-4 rounded-xl border-2 border-amber-500/50 bg-amber-500/10 backdrop-blur-sm"
  >
    <div className="flex items-start gap-3">
      <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <h4 className="font-bold text-amber-500 text-sm uppercase tracking-wide">
          {t('auth.minorAlert.title')}
        </h4>
        <p className="text-sm text-muted-foreground mt-1">
          {t('auth.minorAlert.description')}
        </p>
        <a
          href={`${API}/uploads/oswiadczenie-rodzica-opiekuna.pdf`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Download className="w-4 h-4" />
          {t('auth.minorAlert.downloadBtn')}
        </a>
      </div>
    </div>
  </motion.div>
);

// Member Card Modal Component - Apple Wallet Store Card style (vertical, ~320x440px)
const MemberCardModal = ({ isOpen, onClose, member, isMainAccount, isActive, td }) => {
  const cardRef = React.useRef(null);
  
  if (!isOpen || !member) return null;
  
  // Phone number with prefix for QR code
  const phoneWithPrefix = member.phone 
    ? `${member.phoneCode || '+48'}${member.phone.replace(/\s/g, '')}` 
    : '';
  
  // Client number from FitNet
  const clientNumber = member.fitnetClientId || member.user_id || member.subaccount_id || 'N/A';
  const fullName = `${member.firstName || ''} ${member.lastName || ''}`.trim();
  
  // Function to download card as image
  const handleDownload = async () => {
    if (!cardRef.current) return;
    
    try {
      // Use html2canvas dynamically
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2, // Higher quality
        useCORS: true,
      });
      
      const link = document.createElement('a');
      link.download = `avepark-card-${fullName.replace(/\s+/g, '-').toLowerCase() || 'member'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Failed to download card:', error);
      alert(td('account.downloadError') || 'Nie udało się pobrać karty');
    }
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.4 }}
            className="relative flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Card - Apple Wallet Store Card style (vertical) */}
            <div 
              ref={cardRef}
              className="relative overflow-hidden rounded-2xl shadow-2xl"
              style={{ 
                width: '320px', 
                minHeight: '440px',
                background: 'linear-gradient(180deg, #0a0a0a 0%, #111111 100%)'
              }}
            >
              {/* Subtle purple gradient overlay */}
              <div 
                className="absolute inset-0"
                style={{
                  background: 'radial-gradient(ellipse at top center, rgba(139, 92, 246, 0.08) 0%, transparent 60%)'
                }}
              />
              
              {/* === HEADER SECTION === */}
              <div className="relative px-5 pt-5 pb-4 flex items-center justify-between border-b border-white/5">
                <Logo className="w-10 h-10 text-white" variant="light" />
                <p className="text-gray-500 text-[10px] uppercase tracking-wider">Member Card</p>
              </div>
              
              {/* === PRIMARY SECTION (Strip image area) === */}
              <div 
                className="relative px-5 py-6 flex flex-col items-center justify-center"
                style={{
                  background: 'linear-gradient(180deg, rgba(139, 92, 246, 0.12) 0%, rgba(124, 58, 237, 0.08) 100%)'
                }}
              >
                {/* Large QR Code */}
                <div className="p-3 bg-white rounded-xl shadow-lg">
                  <QRCodeSVG 
                    value={phoneWithPrefix || clientNumber.toString()}
                    size={140}
                    level="H"
                    includeMargin={false}
                  />
                </div>
              </div>
              
              {/* === SECONDARY FIELDS === */}
              <div className="relative px-5 py-5 space-y-4 border-t border-white/5">
                {/* Name */}
                <div>
                  <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">{td('account.memberName') || 'Imię i nazwisko'}</p>
                  <p className="text-white text-xl font-bold tracking-wide">
                    {fullName || td('account.unknownUser')}
                  </p>
                </div>
                
                {/* Client Number */}
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">{td('account.clientNumber')}</p>
                    <p className="text-purple-400 font-mono text-lg font-semibold">{clientNumber}</p>
                  </div>
                  {phoneWithPrefix && (
                    <div className="text-right">
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">{td('account.phone')}</p>
                      <p className="text-gray-300 font-mono text-sm">{phoneWithPrefix}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* === FOOTER === */}
              <div className="relative px-5 py-4 mt-auto border-t border-white/5 flex items-center justify-between">
                <span className="text-gray-600 text-[10px]">ul. Krakowiaków 80/98, Warszawa</span>
                <span className="text-purple-500/60 text-[10px] font-medium">avepark.pl</span>
              </div>
            </div>
            
            {/* Download button - outside the card */}
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl transition-colors text-sm font-medium shadow-lg"
              data-testid="download-card-button"
            >
              <Download className="w-4 h-4" />
              {td('account.downloadCard') || 'Pobierz kartę'}
            </button>
            
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-0 right-0 translate-x-1/3 -translate-y-1/3 w-10 h-10 bg-gray-800 hover:bg-gray-700 text-white rounded-full flex items-center justify-center border border-gray-600 transition-colors shadow-lg"
              data-testid="close-card-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const AccountPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { td } = useTranslations(); // Dynamic translations from admin panel
  const { user, token, isAuthenticated, logout, loading: authLoading } = useAuth();
  const { isAvailable: isFitnetAvailable } = useFitnet();
  const navigate = useNavigate();
  const lang = i18n.language;
  
  // Combined translation function - database first (admin panel), then i18n fallback
  const t = (key, fallback = null) => {
    const dbValue = td(key);
    if (dbValue && dbValue !== key) return dbValue;
    // Try i18n as fallback
    const i18nVal = ti18n(key);
    if (i18nVal && i18nVal !== key) return i18nVal;
    return fallback || key;
  };

  const [subaccounts, setSubaccounts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [activePasses, setActivePasses] = useState([]);
  const [pendingPasses, setPendingPasses] = useState([]);
  const [expiredPasses, setExpiredPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSubaccount, setShowAddSubaccount] = useState(false);
  const [submittingSubaccount, setSubmittingSubaccount] = useState(false);
  const [newSubaccount, setNewSubaccount] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    phone: '',
    phoneCode: '+48',
    gender: ''
  });
  const [subaccountError, setSubaccountError] = useState('');

  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Marketing consent state
  const [marketingConsent, setMarketingConsent] = useState(user?.marketingConsent || false);
  const [newsletterConsent, setNewsletterConsent] = useState(user?.newsletterConsent || false);
  const [consentSaving, setConsentSaving] = useState(false);
  
  // Preferred language state
  const [preferredLanguage, setPreferredLanguage] = useState(user?.preferredLanguage || 'pl');
  const [languageSaving, setLanguageSaving] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([]);

  // Cancel booking modal state
  const [cancelModal, setCancelModal] = useState({ open: false, bookingId: null, isPaid: false });
  const [cancelResult, setCancelResult] = useState({ show: false, success: false, message: '' });

  // Pass activation state
  const [activateUid, setActivateUid] = useState('');
  const [activateForPerson, setActivateForPerson] = useState('');
  const [activateError, setActivateError] = useState('');
  const [activateSuccess, setActivateSuccess] = useState(false);
  const [activatingPassId, setActivatingPassId] = useState(null);  // NEW: Track which pass is being activated
  const [activationDeadlineDays, setActivationDeadlineDays] = useState(60);  // NEW: From admin settings

  // Member card modal state
  const [cardModal, setCardModal] = useState({ open: false, member: null, isMainAccount: false });

  // Filter states
  const [bookingFilter, setBookingFilter] = useState('upcoming');
  const [passFilter, setPassFilter] = useState('active');

  // Loyalty points state
  const [loyaltyPoints, setLoyaltyPoints] = useState({ balance: 0, pointsExpiringSoon: 0, history: [] });
  const [loadingPoints, setLoadingPoints] = useState(true);
  const [loyaltyProgramActive, setLoyaltyProgramActive] = useState(false);

  // Fetch public settings (including loyalty program status)
  const fetchPublicSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      setLoyaltyProgramActive(response.data.loyaltyProgramActive || false);
    } catch (error) {
      console.error('Failed to fetch public settings:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [subRes, bookRes, passRes, loyaltyRes] = await Promise.all([
        axios.get(`${API}/subaccounts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/passes/my`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/loyalty/balance`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { balance: 0, history: [] } }))
      ]);
      setSubaccounts(subRes.data);
      setBookings(bookRes.data);
      // New API structure with separate arrays
      setActivePasses(passRes.data.activePasses || []);
      setPendingPasses(passRes.data.pendingPasses || []);
      setLoyaltyPoints(loyaltyRes.data);
      setLoadingPoints(false);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Wait for auth loading to complete before checking authentication
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
    fetchPublicSettings();
    
    // Check for payment success callback
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    const paymentType = params.get('type');
    const paymentId = params.get('id');
    
    if (paymentStatus === 'success' && paymentId) {
      // Show success message and refresh data
      setTimeout(() => {
        alert(lang === 'pl' 
          ? `Płatność została zarejestrowana! Oczekuj potwierdzenia.` 
          : `Payment registered! Awaiting confirmation.`);
        // Remove query params from URL
        navigate('/account', { replace: true });
        fetchData();
      }, 1000);
    }
  }, [isAuthenticated, authLoading, fetchData, navigate, lang]);

  useEffect(() => {
    if (user) {
      setMarketingConsent(user.marketingConsent || false);
      setNewsletterConsent(user.newsletterConsent || false);
      setPreferredLanguage(user.preferredLanguage || 'pl');
    }
  }, [user]);

  // Fetch available languages
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const res = await axios.get(`${API}/languages`);
        // API already returns only active languages
        setAvailableLanguages(res.data);
      } catch (error) {
        console.error('Failed to fetch languages:', error);
      }
    };
    fetchLanguages();
    
    // Also fetch public settings for activation deadline
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings/public`);
        if (res.data.passActivationDeadlineDays) {
          setActivationDeadlineDays(res.data.passActivationDeadlineDays);
        }
      } catch (error) {
        console.error('Failed to fetch public settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleAddSubaccount = async () => {
    if (submittingSubaccount) return; // Prevent double submission
    
    setSubaccountError('');
    
    // Validation - all fields required
    if (!newSubaccount.firstName.trim()) {
      setSubaccountError(t('errors.firstNameRequired', 'First name is required'));
      return;
    }
    if (!newSubaccount.lastName.trim()) {
      setSubaccountError(t('errors.lastNameRequired', 'Last name is required'));
      return;
    }
    if (!newSubaccount.birthDate) {
      setSubaccountError(t('errors.birthDateRequired', 'Birth date is required'));
      return;
    }
    if (!newSubaccount.phone.trim()) {
      setSubaccountError(t('errors.phoneRequired', 'Phone number is required'));
      return;
    }
    if (!newSubaccount.gender) {
      setSubaccountError(t('errors.genderRequired', 'Gender is required'));
      return;
    }

    setSubmittingSubaccount(true);
    try {
      await axios.post(`${API}/subaccounts`, {
        ...newSubaccount,
        phone: newSubaccount.phone,
        phoneCode: newSubaccount.phoneCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAddSubaccount(false);
      setNewSubaccount({ firstName: '', lastName: '', birthDate: '', phone: '', phoneCode: '+48', gender: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to add subaccount:', error);
      setSubaccountError(error.response?.data?.error || t('errors.addPersonFailed', 'Failed to add person'));
    } finally {
      setSubmittingSubaccount(false);
    }
  };

  const handleCancelBooking = async (id) => {
    // Find the booking to check if cancellation is allowed
    const booking = bookings.find(b => b.booking_id === id);
    if (booking) {
      const bookingDateTime = new Date(`${booking.date}T${booking.time}:00`);
      const now = new Date();
      const hoursUntil = (bookingDateTime - now) / (1000 * 60 * 60);
      
      if (hoursUntil < 24) {
        alert(t('errors.cannotCancelBooking24h', 'Cannot cancel booking less than 24 hours before start time.'));
        return;
      }
    }
    
    // Open modal instead of window.confirm
    setCancelModal({ 
      open: true, 
      bookingId: id, 
      isPaid: booking?.paymentStatus === 'paid' 
    });
  };

  const confirmCancelBooking = async () => {
    const { bookingId } = cancelModal;
    setCancelModal({ open: false, bookingId: null, isPaid: false });
    
    try {
      const response = await axios.delete(`${API}/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const message = response.data.refundInitiated 
        ? t('booking.cancelledWithRefund', 'Booking cancelled. Refund has been initiated.')
        : t('booking.cancelledNoRefund', 'Booking has been cancelled.');
      
      setCancelResult({ show: true, success: true, message });
      fetchData();
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      const errorMsg = error.response?.data?.errorPl || error.response?.data?.error || 
        t('errors.cancelBookingFailed', 'Failed to cancel booking');
      setCancelResult({ show: true, success: false, message: errorMsg });
    }
  };

  const handlePayBooking = async (bookingId) => {
    try {
      const response = await axios.post(`${API}/payments/booking/${bookingId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.redirectUri) {
        // Redirect to PayU payment page
        window.location.href = response.data.redirectUri;
      } else {
        alert(t('errors.paymentInitFailed', 'Failed to initialize payment'));
      }
    } catch (error) {
      console.error('Failed to initiate payment:', error);
      alert(t('errors.paymentFailed', 'Payment error'));
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordSuccess(false);

    if (passwords.new !== passwords.confirm) {
      setPasswordError(t('errors.passwordsNotMatch', 'Passwords do not match'));
      return;
    }

    if (passwords.new.length < 6) {
      setPasswordError(t('errors.passwordMinLength', 'Password must be at least 6 characters'));
      return;
    }

    try {
      await axios.post(`${API}/auth/change-password`, {
        currentPassword: passwords.current,
        newPassword: passwords.new
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPasswordSuccess(true);
      setPasswords({ current: '', new: '', confirm: '' });
      setTimeout(() => {
        setShowPasswordChange(false);
        setPasswordSuccess(false);
      }, 2000);
    } catch (error) {
      // Map backend errors to user-friendly messages
      const errorMsg = error.response?.data?.error;
      let translatedError = t('errors.passwordChangeFailed', 'Password change failed');
      
      if (errorMsg === 'Current password is incorrect') {
        translatedError = t('errors.currentPasswordIncorrect', 'Current password is incorrect');
      } else if (errorMsg === 'New password must be at least 8 characters') {
        translatedError = t('errors.passwordMin8Chars', 'New password must be at least 8 characters');
      } else if (errorMsg === 'Cannot change password for OAuth accounts') {
        translatedError = t('errors.cannotChangeOAuthPassword', 'Cannot change password for OAuth accounts');
      }
      
      setPasswordError(translatedError);
    }
  };

  const handleMarketingConsentChange = async (value) => {
    setConsentSaving(true);
    try {
      await axios.put(`${API}/auth/marketing-consent`, { consent: value }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000 // 15 second timeout
      });
      setMarketingConsent(value);
      
      // Show success toast with translation
      if (value) {
        toast.success(td('account.marketingSubscribed', 'Zapisano do komunikacji marketingowej'));
      } else {
        toast.success(td('account.marketingUnsubscribed', 'Wypisano z komunikacji marketingowej'));
      }
    } catch (error) {
      console.error('Failed to update consent:', error);
      toast.error(td('account.consentUpdateError', 'Nie udało się zaktualizować zgody. Spróbuj ponownie.'));
      // Revert the toggle on error
      setMarketingConsent(!value);
    } finally {
      setConsentSaving(false);
    }
  };

  const handleNewsletterConsentChange = async (value) => {
    setConsentSaving(true);
    try {
      await axios.put(`${API}/auth/newsletter-consent`, { consent: value }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000 // 15 second timeout
      });
      setNewsletterConsent(value);
      
      // Show success toast with translation
      if (value) {
        toast.success(td('account.newsletterSubscribed', 'Zapisano do newslettera'));
      } else {
        toast.success(td('account.newsletterUnsubscribed', 'Wypisano z newslettera'));
      }
    } catch (error) {
      console.error('Failed to update newsletter consent:', error);
      toast.error(td('account.consentUpdateError', 'Nie udało się zaktualizować zgody. Spróbuj ponownie.'));
      // Revert the toggle on error
      setNewsletterConsent(!value);
    } finally {
      setConsentSaving(false);
    }
  };

  const handlePreferredLanguageChange = async (value) => {
    setLanguageSaving(true);
    try {
      await axios.put(`${API}/auth/preferred-language`, { language: value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferredLanguage(value);
    } catch (error) {
      console.error('Failed to update preferred language:', error);
    } finally {
      setLanguageSaving(false);
    }
  };

  const handleActivatePass = async () => {
    setActivateError('');
    setActivateSuccess(false);

    if (!activateUid) {
      setActivateError(t('account.enterPassUid', 'Enter pass UID'));
      return;
    }

    try {
      await axios.post(`${API}/passes/activate`, {
        passUid: activateUid,
        forSubaccountId: activateForPerson || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivateSuccess(true);
      setActivateUid('');
      setActivateForPerson('');
      fetchData();
      setTimeout(() => setActivateSuccess(false), 3000);
    } catch (error) {
      setActivateError(error.response?.data?.detail || t('account.failedToActivate', 'Failed to activate pass'));
    }
  };

  // NEW: Activate user's own pending pass (not gift)
  const handleActivateMyPass = async (purchaseId) => {
    if (!purchaseId) return;
    
    setActivatingPassId(purchaseId);
    
    try {
      const response = await axios.post(`${API}/passes/activate-my-pass`, {
        purchaseId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const passName = response.data.passName || t('passes.pass', 'Karnet');
        
        if (response.data.alreadyActive) {
          // Pass was already activated (e.g., by CRON)
          toast.info(t('passes.passAlreadyActivated', 'Karnet "{passName}" był już wcześniej aktywowany.').replace('{passName}', passName));
        } else {
          // Pass just activated
          toast.success(t('passes.passActivatedSuccess', 'Karnet "{passName}" został aktywowany!').replace('{passName}', passName));
        }
        // Refresh data to update the list
        fetchData();
      }
    } catch (error) {
      console.error('Failed to activate pass:', error);
      // Use translated error message from backend if available, otherwise use frontend translation
      const errorMessage = error.response?.data?.errorPl || 
        error.response?.data?.error || 
        t('passes.activationFailed', 'Nie udało się aktywować karnetu');
      toast.error(errorMessage);
    } finally {
      setActivatingPassId(null);
    }
  };

  // Get person name by ID
  const getPersonName = (personId) => {
    if (!personId || personId === user?.user_id) {
      return `${user?.firstName} ${user?.lastName} (${t('account.me', 'Me')})`;
    }
    const sub = subaccounts.find(s => s.subaccount_id === personId);
    return sub ? `${sub.firstName} ${sub.lastName}` : personId;
  };

  // All people (main + subaccounts) for selection
  const allPeople = [
    { id: user?.user_id, name: `${user?.firstName} ${user?.lastName}`, label: t('account.mainAccount', '(Main account)') },
    ...subaccounts.map(s => ({ id: s.subaccount_id, name: `${s.firstName} ${s.lastName}`, label: '' }))
  ];

  // Filtered bookings
  const filteredBookings = bookings
    .filter(booking => {
      const bookingDate = new Date(booking.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (bookingFilter === 'upcoming') return bookingDate >= today;
      if (bookingFilter === 'past') return bookingDate < today;
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return bookingFilter === 'past' ? dateB - dateA : dateA - dateB;
    });

  // Filtered passes - now using new API structure
  // Filter passes based on expiry and separate active from expired
  const { currentActivePasses, currentExpiredPasses } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const active = [];
    const expired = [];
    
    activePasses.forEach(pass => {
      const validTo = pass.fitnetValidTo || pass.validTo;
      if (validTo) {
        const expiryDate = new Date(validTo);
        if (expiryDate < today) {
          expired.push({ ...pass, isExpired: true });
        } else {
          active.push(pass);
        }
      } else {
        // No expiry date - consider active
        active.push(pass);
      }
    });
    
    return { currentActivePasses: active, currentExpiredPasses: expired };
  }, [activePasses]);

  // 'active' = all active passes from FitNet (both online after activation + stacjonarne)
  // 'pending' = online purchases waiting for activation
  // 'expired' = passes with validTo in the past
  const filteredPasses = (() => {
    if (passFilter === 'active') {
      return currentActivePasses;
    }
    if (passFilter === 'online') {
      // Only pending passes (waiting for activation)
      return pendingPasses;
    }
    if (passFilter === 'expired') {
      return currentExpiredPasses;
    }
    // 'all' - combine all
    return [...currentActivePasses, ...pendingPasses, ...currentExpiredPasses];
  })();

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 md:px-12 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{t('account.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen pt-28 sm:pt-32 pb-20 px-4 sm:px-6 md:px-12" data-testid="account-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-['Syne'] font-bold mb-3 sm:mb-4">{t('account.title')}</h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            {t('account.manageAccountDescription', 'Manage your account and bookings')}
          </p>
        </div>

        {/* Loyalty Points Card - only show when program is active */}
        {loyaltyProgramActive && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-yellow-500/20 via-amber-500/10 to-orange-500/20 border border-yellow-500/30"
          >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Award className="w-7 h-7 text-yellow-500" />
              </div>
              <div>
                <p className="text-sm text-yellow-500/80 font-medium uppercase tracking-wide">
                  {t('loyalty.yourPoints', 'Twoje punkty')}
                </p>
                <p className="text-3xl font-bold text-white">
                  {loadingPoints ? (
                    <span className="inline-block w-16 h-8 bg-white/10 animate-pulse rounded" />
                  ) : (
                    loyaltyPoints.balance.toLocaleString()
                  )}
                </p>
              </div>
            </div>
            
            {loyaltyPoints.pointsExpiringSoon > 0 && (
              <div className="px-4 py-2 rounded-lg bg-amber-500/20 border border-amber-500/30">
                <p className="text-xs text-amber-400">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  {loyaltyPoints.pointsExpiringSoon} {t('loyalty.pointsExpiringSoon', 'punktów wygasa wkrótce')}
                </p>
              </div>
            )}
            
            <div className="text-sm text-muted-foreground">
              {t('loyalty.collectPointsInfo', 'Zbieraj punkty za zakupy i rezerwacje!')}
            </div>
          </div>
          
          {loyaltyPoints.history && loyaltyPoints.history.length > 0 && (
            <div className="mt-4 pt-4 border-t border-yellow-500/20">
              <p className="text-xs text-yellow-500/60 mb-2">{t('loyalty.pointsHistory', 'Historia punktów')}:</p>
              <div className="flex flex-wrap gap-2">
                {loyaltyPoints.history.slice(0, 3).map((item, idx) => (
                  <div key={idx} className="text-xs px-3 py-1.5 rounded-full bg-white/5 text-gray-400">
                    <span className={item.points > 0 ? 'text-green-400' : 'text-red-400'}>
                      {item.points > 0 ? '+' : ''}{item.points}
                    </span>
                    {' '}{item.productName || item.description}
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
        )}

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="w-full grid grid-cols-2 sm:grid-cols-4 gap-1 p-1.5 sm:p-2 mb-6 sm:mb-8 bg-gray-100 dark:bg-white/10 rounded-xl">
            <TabsTrigger value="profile" className="flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm py-2" data-testid="tab-profile">
              <User className="w-4 h-4" /> <span className="hidden xs:inline">{t('account.profile')}</span><span className="xs:hidden">{t('account.profileShort', 'Profil')}</span>
            </TabsTrigger>
            <TabsTrigger value="subaccounts" className="flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm py-2" data-testid="tab-subaccounts">
              <Users className="w-4 h-4" /> <span className="hidden xs:inline">{t('account.subaccounts')}</span><span className="xs:hidden">{t('account.usersShort', 'Osoby')}</span>
            </TabsTrigger>
            <TabsTrigger value="bookings" className="flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm py-2" data-testid="tab-bookings">
              <Calendar className="w-4 h-4" /> <span className="hidden xs:inline">{t('account.bookings')}</span><span className="xs:hidden">{t('account.bookingsShort', 'Rezerw.')}</span>
            </TabsTrigger>
            <TabsTrigger value="passes" className="flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm py-2" data-testid="tab-passes">
              <CreditCard className="w-4 h-4" /> <span className="hidden xs:inline">{t('account.passes')}</span><span className="xs:hidden">{t('account.passesShort', 'Karnety')}</span>
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="glass-card p-4 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">{t('account.profile')}</h2>
              <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="space-y-4">
                  <div>
                    <Label className="text-muted-foreground">{t('auth.firstName')}</Label>
                    <p className="text-lg font-medium">{user?.firstName}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">{t('auth.lastName')}</Label>
                    <p className="text-lg font-medium">{user?.lastName}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-muted-foreground">{t('auth.phone')}</Label>
                    <p className="text-lg font-medium">{user?.phoneCode} {user?.phone}</p>
                  </div>
                   <div>
                    <Label className="text-muted-foreground">{t('auth.email')}</Label>
                    <p className="text-lg font-medium">{user?.email}</p>
                  </div>
                  {/* FitNet ID and Points hidden per user request */}
                </div>
              </div>

              {/* Divider */}
              <div className="foreground opacity-20 border-t my-8" />

              {/* Password Change - inline in Profile */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <Lock className="w-6 h-6 text-primary" />
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white">
                      {t('account.changePassword', 'Change password')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('account.changePasswordDescription', 'Change your current password')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPasswordChange(!showPasswordChange)}
                  className="btn-secondary text-sm py-2 px-4"
                >
                  {showPasswordChange ? t('common.cancel') : t('account.changeBtn', 'Change')}
                </button>
              </div>

              {showPasswordChange && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-4 mb-8"
                >
                  {passwordError && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                      {passwordError}
                    </div>
                  )}
                  {passwordSuccess && (
                    <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">
                      {t('account.passwordChanged', 'Password changed successfully!')}
                    </div>
                  )}
                  <div>
                    <Label>{t('account.currentPassword', 'Current password')}</Label>
                    <Input
                      type="password"
                      value={passwords.current}
                      onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                      className="input-glass mt-2"
                    />
                  </div>
                  <div>
                    <Label>{t('account.newPassword', 'New password')}</Label>
                    <Input
                      type="password"
                      value={passwords.new}
                      onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                      className="input-glass mt-2"
                    />
                  </div>
                  <div>
                    <Label>{t('account.confirmNewPassword', 'Confirm new password')}</Label>
                    <Input
                      type="password"
                      value={passwords.confirm}
                      onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                      className="input-glass mt-2"
                    />
                  </div>
                  <button onClick={handlePasswordChange} className="btn-primary">
                    {t('account.saveNewPassword', 'Save new password')}
                  </button>
                </motion.div>
              )}

              {/* Divider */}
              <div className="foreground opacity-20 border-t my-8" />

              {/* Marketing Consent */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Bell className="w-6 h-6 text-primary" />
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white">
                      {t('account.marketingConsent', 'Marketing consent')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('account.marketingDescription', 'Receive information about promotions and news')}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={marketingConsent}
                  onCheckedChange={handleMarketingConsentChange}
                  disabled={consentSaving}
                  data-testid="marketing-consent-switch"
                />
              </div>

              {/* Divider */}
              <div className="foreground opacity-20 border-t my-8" />

              {/* Newsletter Consent */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Mail className="w-6 h-6 text-primary" />
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white">
                      {t('account.newsletter', 'Newsletter')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('account.newsletterDescription', 'Receive newsletter with updates and news')}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={newsletterConsent}
                  onCheckedChange={handleNewsletterConsentChange}
                  disabled={consentSaving}
                  data-testid="newsletter-consent-switch"
                />
              </div>

              {/* Divider */}
              <div className="foreground opacity-20 border-t my-8" />

              {/* Preferred Language */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                  </svg>
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white">
                      {t('account.preferredLanguage', 'Preferred language')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('account.preferredLanguageDescription', 'Choose language for email notifications')}
                    </p>
                  </div>
                </div>
                <Select 
                  value={preferredLanguage} 
                  onValueChange={handlePreferredLanguageChange}
                  disabled={languageSaving}
                >
                  <SelectTrigger className="w-[180px]" data-testid="preferred-language-select">
                    <SelectValue>
                      {availableLanguages.find(l => l.code === preferredLanguage)?.nativeName || preferredLanguage.toUpperCase()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {availableLanguages.map(lang => (
                      <SelectItem key={lang.code} value={lang.code}>
                        <span className="inline-flex items-center gap-1.5"><FlagIcon code={lang.code} size={16} /> {lang.nativeName}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>

          {/* Subaccounts Tab */}
          <TabsContent value="subaccounts" className="mb-8">
            <div className="glass-card p-4 sm:p-8 mb-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                <h2 className="text-xl sm:text-2xl font-bold">{t('account.subaccounts')}</h2>
                <button
                  onClick={() => setShowAddSubaccount(true)}
                  className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto text-sm sm:text-base py-2.5 sm:py-2"
                  data-testid="add-subaccount-button"
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5" /> {t('account.addSubaccount')}
                </button>
              </div>

              {/* Add Subaccount Form */}
              {showAddSubaccount && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mb-6 p-6 border border-primary/30 rounded-2xl"
                >
                  {subaccountError && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                      {subaccountError}
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label>{t('auth.firstName')} *</Label>
                      <Input
                        value={newSubaccount.firstName}
                        onChange={(e) => setNewSubaccount({ ...newSubaccount, firstName: e.target.value })}
                        className="input-glass mt-2"
                        data-testid="new-subaccount-firstname"
                        required
                      />
                    </div>
                    <div>
                      <Label>{t('auth.lastName')} *</Label>
                      <Input
                        value={newSubaccount.lastName}
                        onChange={(e) => setNewSubaccount({ ...newSubaccount, lastName: e.target.value })}
                        className="input-glass mt-2"
                        data-testid="new-subaccount-lastname"
                        required
                      />
                    </div>
                    <div>
                      <Label>{t('auth.birthDate')} *</Label>
                      <Input
                        type="date"
                        value={newSubaccount.birthDate}
                        onChange={(e) => setNewSubaccount({ ...newSubaccount, birthDate: e.target.value })}
                        className="input-glass mt-2"
                        data-testid="new-subaccount-birthdate"
                        required
                      />
                      {/* Minor Alert for subaccount */}
                      {newSubaccount.birthDate && calculateAge(newSubaccount.birthDate) < 18 && (
                        <MinorAlert t={t} />
                      )}
                    </div>
                    <div>
                      <Label>{t('auth.phone')} *</Label>
                      <div className="flex gap-2 mt-2">
                        <PhonePrefixSelect 
                          value={newSubaccount.phoneCode} 
                          onChange={(v) => setNewSubaccount({ ...newSubaccount, phoneCode: v })}
                          className="input-glass w-32"
                        />
                        <Input
                          value={newSubaccount.phone}
                          onChange={(e) => setNewSubaccount({ ...newSubaccount, phone: e.target.value })}
                          className="input-glass flex-1"
                          placeholder="123 456 789"
                          data-testid="new-subaccount-phone"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label>{t('auth.gender')} *</Label>
                      <Select 
                        value={newSubaccount.gender} 
                        onValueChange={(v) => setNewSubaccount({ ...newSubaccount, gender: v })}
                      >
                        <SelectTrigger className="input-glass mt-2" data-testid="new-subaccount-gender">
                          <SelectValue placeholder={t('common.select', 'Select')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">{t('auth.male')}</SelectItem>
                          <SelectItem value="female">{t('auth.female')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-4">
                    <button
                      onClick={() => setShowAddSubaccount(false)}
                      className="btn-secondary flex items-center gap-2"
                    >
                      <X className="w-4 h-4" /> {t('common.cancel')}
                    </button>
                    <button
                      onClick={handleAddSubaccount}
                      disabled={submittingSubaccount}
                      className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="save-subaccount-button"
                    >
                      {submittingSubaccount ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                      {submittingSubaccount ? t('common.saving', 'Zapisywanie...') : t('common.save')}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Subaccounts List - with Main Account and Show Card button */}
              <div className="flex flex-col gap-4">
                {/* Main Account - always shown first */}
                <div
                  className="p-4 bg-gradient-to-r from-purple-500/10 to-violet-500/10 border-2 border-purple-500/30 rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
                  data-testid="main-account-card"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
                      <Crown className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-foreground">{user?.firstName} {user?.lastName}</p>
                        <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs font-medium rounded-full uppercase">
                          {td('account.mainAccountBadge')}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        {user?.birthDate && (
                          <span>{new Date(user.birthDate).toLocaleDateString(lang === 'pl' ? 'pl-PL' : lang === 'de' ? 'de-DE' : lang === 'uk' ? 'uk-UA' : 'en-US')}</span>
                        )}
                        {user?.phone && (
                          <>
                            <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
                            <span>{user.phoneCode || '+48'} {user.phone}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setCardModal({ open: true, member: user, isMainAccount: true, isActive: true })}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-xl transition-colors text-sm font-medium"
                    data-testid="show-card-main"
                  >
                    <QrCode className="w-4 h-4" />
                    {td('account.showCard')}
                  </button>
                </div>

                {/* Subaccounts */}
                {subaccounts.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    {td('account.noPeopleAdded')}
                  </p>
                ) : (
                  subaccounts.map((sub) => (
                    <div
                      key={sub.subaccount_id}
                      className="relative p-4 bg-black/5 dark:bg-white/5 rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
                      data-testid={`subaccount-${sub.subaccount_id}`}
                    >
                      {/* Green checkmark for active subaccounts - positioned at top-left corner */}
                      {sub.isActivated && (
                        <div className="absolute -top-1.5 -left-1.5 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-md border-2 border-background z-10">
                          <Check className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                          <Users className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold text-foreground">{sub.firstName} {sub.lastName}</p>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            {sub.birthDate && (
                              <span>{new Date(sub.birthDate).toLocaleDateString(lang === 'pl' ? 'pl-PL' : lang === 'de' ? 'de-DE' : lang === 'uk' ? 'uk-UA' : 'en-US')}</span>
                            )}
                            {sub.phone && (
                              <>
                                <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
                                <span>{sub.phoneCode || '+48'} {sub.phone}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 w-full sm:w-auto">
                        <button
                          onClick={() => setCardModal({ open: true, member: sub, isMainAccount: false, isActive: sub.isActivated })}
                          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-foreground rounded-xl transition-colors text-sm font-medium"
                          data-testid={`show-card-${sub.subaccount_id}`}
                        >
                          <QrCode className="w-4 h-4" />
                          {td('account.showCard')}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Member Card Modal */}
            <MemberCardModal 
              isOpen={cardModal.open}
              onClose={() => setCardModal({ open: false, member: null, isMainAccount: false, isActive: false })}
              member={cardModal.member}
              isMainAccount={cardModal.isMainAccount}
              isActive={cardModal.isActive}
              td={td}
            />
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="mb-8">
            <div className="glass-card p-8 mb-6">
              <h2 className="text-2xl font-bold mb-6">{t('account.bookings')}</h2>
              
              {/* Filter buttons */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setBookingFilter('upcoming')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border-2 ${
                    bookingFilter === 'upcoming' 
                      ? 'bg-primary text-white border-primary' 
                      : 'bg-white/5 hover:bg-white/10 border-transparent text-gray-600 dark:text-gray-300'
                  }`}
                  data-testid="filter-upcoming"
                >
                  {t('account.upcomingBookings', 'Upcoming')}
                </button>
                <button
                  onClick={() => setBookingFilter('past')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border-2 ${
                    bookingFilter === 'past' 
                      ? 'bg-primary text-white border-primary' 
                      : 'bg-white/5 hover:bg-white/10 border-transparent text-gray-600 dark:text-gray-300'
                  }`}
                  data-testid="filter-past"
                >
                  {t('account.pastBookings', 'Past')}
                </button>
                <button
                  onClick={() => setBookingFilter('all')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border-2 ${
                    bookingFilter === 'all' 
                      ? 'bg-primary text-white border-primary' 
                      : 'bg-white/5 hover:bg-white/10 border-transparent text-gray-600 dark:text-gray-300'
                  }`}
                  data-testid="filter-all"
                >
                  {t('account.allBookings', 'All')}
                </button>
              </div>

              <div className="flex flex-col gap-4">
                {filteredBookings.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {bookingFilter === 'upcoming' 
                      ? t('account.noUpcomingBookings', 'No upcoming bookings')
                      : bookingFilter === 'past'
                      ? t('account.noPastBookings', 'No past bookings')
                      : t('account.noBookings', 'No bookings yet')}
                  </p>
                ) : (
                  filteredBookings.map((booking) => (
                    <div
                      key={booking.booking_id}
                      className={`p-4 bg-black/5 dark:bg-white/5 rounded-xl ${booking.status === 'cancelled' ? 'opacity-50' : ''}`}
                      data-testid={`booking-${booking.booking_id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-lg text-foreground">
                            {booking.group?.name || 'AvePark'} - {booking.classType?.name || booking.slotName || 'Sesja'}
                          </p>
                          <p className="text-muted-foreground">
                            {new Date(booking.date).toLocaleDateString(lang === 'pl' ? 'pl-PL' : 'en-US', {
                              weekday: 'long', day: 'numeric', month: 'long'
                            })}
                          </p>
                          <p className="text-muted-foreground">{booking.time} - {booking.endTime}</p>
                          {/* Show slot name if available */}
                          {booking.slotName && (
                            <p className="text-sm text-muted-foreground">{booking.slotName}</p>
                          )}
                          {/* Show assigned person */}
                          <p className="text-sm text-primary mt-2 flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {getPersonName(booking.for_user_id)}
                          </p>
                          {/* Show payment status */}
                          {booking.paymentStatus && booking.paymentStatus !== 'paid' && booking.price > 0 && (
                            <p className="text-sm text-yellow-500 flex items-center gap-1 mt-1">
                              <CreditCard className="w-4 h-4" />
                              {t('booking.awaitingPayment', 'Awaiting payment')}
                            </p>
                          )}
                          {/* Show refund status */}
                          {booking.refundStatus === 'refunded' && (
                            <p className="text-sm text-green-500 flex items-center gap-1 mt-1">
                              <Check className="w-4 h-4" />
                              {t('booking.refundInitiated', 'Refund initiated')}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-medium ${
                            booking.status === 'confirmed' ? 'text-green-500' : 
                            booking.status === 'cancelled' ? 'text-red-500' : 'text-yellow-500'
                          }`}>
                            {booking.status === 'confirmed' ? t('booking.confirmed', 'Confirmed') :
                             booking.status === 'cancelled' ? t('booking.cancelled', 'Cancelled') : booking.status}
                          </p>
                          <p className="text-lg font-bold text-primary">{booking.price} {t('common.pln')}</p>
                          {/* Pay button removed - users should create new booking */}
                          {booking.paymentStatus === 'paid' && (
                            <span className="mt-2 inline-flex items-center gap-1 px-3 py-1 bg-green-500/20 text-green-500 rounded-full text-xs font-medium">
                              <Check className="w-3 h-3" />
                              {t('booking.paid', 'Paid')}
                            </span>
                          )}
                          {/* Cancel button - only if > 24h before booking */}
                          {booking.status === 'confirmed' && isFitnetAvailable && (() => {
                            const bookingDateTime = new Date(`${booking.date}T${booking.time}:00`);
                            const hoursUntil = (bookingDateTime - new Date()) / (1000 * 60 * 60);
                            return hoursUntil > 24;
                          })() && (
                            <button
                              onClick={() => handleCancelBooking(booking.booking_id)}
                              className="mt-3 px-3 py-1.5 text-sm text-red-400 border border-red-400/50 rounded-lg hover:bg-red-400/10 transition-colors"
                              data-testid={`cancel-booking-${booking.booking_id}`}
                            >
                              {t('common.cancel')}
                            </button>
                          )}
                          {/* Show "cannot cancel" message if < 24h */}
                          {booking.status === 'confirmed' && (() => {
                            const bookingDateTime = new Date(`${booking.date}T${booking.time}:00`);
                            const hoursUntil = (bookingDateTime - new Date()) / (1000 * 60 * 60);
                            return hoursUntil > 0 && hoursUntil <= 24;
                          })() && (
                            <p className="mt-2 text-xs text-muted-foreground">
                              {t('booking.cannotCancel', 'Cannot cancel')}
                            </p>
                          )}
                          {/* Show message when FitNet is unavailable */}
                          {booking.status === 'confirmed' && !isFitnetAvailable && (
                            <p className="mt-2 text-xs text-orange-400">
                              {t('maintenance.cannotCancelNow', 'Anulowanie tymczasowo niedostępne')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Passes Tab */}
          <TabsContent value="passes" className="mb-8">
            <div className="glass-card p-8 mb-6">
              <h2 className="text-2xl font-bold mb-6">{t('account.passes')}</h2>
              
              {/* Filter buttons */}
              <div className="flex gap-2 mb-6 flex-wrap">
                <button
                  onClick={() => setPassFilter('active')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border-2 ${
                    passFilter === 'active' 
                      ? 'bg-primary text-white border-primary' 
                      : 'bg-white/5 hover:bg-white/10 border-transparent text-gray-600 dark:text-gray-300'
                  }`}
                  data-testid="filter-active-passes"
                >
                  {t('passes.activePasses', 'Active')} ({currentActivePasses.length})
                </button>
                <button
                  onClick={() => setPassFilter('online')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border-2 ${
                    passFilter === 'online' 
                      ? 'bg-primary text-white border-primary' 
                      : 'bg-white/5 hover:bg-white/10 border-transparent text-gray-600 dark:text-gray-300'
                  }`}
                  data-testid="filter-online-passes"
                >
                  {t('passes.pendingActivation', 'Pending activation')} ({pendingPasses.length})
                </button>
                {currentExpiredPasses.length > 0 && (
                  <button
                    onClick={() => setPassFilter('expired')}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border-2 ${
                      passFilter === 'expired' 
                        ? 'bg-primary text-white border-primary' 
                        : 'bg-white/5 hover:bg-white/10 border-transparent text-gray-600 dark:text-gray-300'
                    }`}
                    data-testid="filter-expired-passes"
                  >
                    {t('passes.expiredPasses', 'Expired')} ({currentExpiredPasses.length})
                  </button>
                )}
                <button
                  onClick={() => setPassFilter('all')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border-2 ${
                    passFilter === 'all' 
                      ? 'bg-primary text-white border-primary' 
                      : 'bg-white/5 hover:bg-white/10 border-transparent text-gray-600 dark:text-gray-300'
                  }`}
                  data-testid="filter-all-passes"
                >
                  {t('passes.allPasses', 'All')}
                </button>
              </div>

              <div className="flex flex-col gap-4">
                {filteredPasses.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {passFilter === 'active' 
                      ? t('passes.noActivePasses', 'No active passes')
                      : passFilter === 'online'
                      ? t('passes.noPendingPasses', 'No passes pending activation')
                      : passFilter === 'expired'
                      ? t('passes.noExpiredPasses', 'No expired passes')
                      : t('passes.noPasses', 'No passes yet')}
                  </p>
                ) : (
                  filteredPasses.map((pass) => {
                    // Use FitNet validity dates if available, otherwise calculate from our data
                    let expiryDateStr = null;
                    let validFromStr = null;
                    
                    if (pass.fitnetValidTo || pass.validTo) {
                      // Use FitNet data directly
                      expiryDateStr = pass.fitnetValidTo || pass.validTo;
                      validFromStr = pass.fitnetValidFrom || pass.validFrom;
                    } else if (pass.createdAt || pass.paidAt || pass.purchasedAt) {
                      // Fallback: Calculate from createdAt + validDays
                      const createdDate = new Date(pass.createdAt || pass.paidAt || pass.purchasedAt);
                      if (!isNaN(createdDate.getTime())) {
                        const expiryDate = new Date(createdDate);
                        if (pass.validDays) {
                          expiryDate.setDate(expiryDate.getDate() + pass.validDays);
                        } else if (pass.validMonths) {
                          expiryDate.setMonth(expiryDate.getMonth() + pass.validMonths);
                        }
                        expiryDateStr = expiryDate.toISOString().split('T')[0];
                      }
                    }
                    
                    // Format dates for display based on user's language
                    const getLocale = () => {
                      switch(lang) {
                        case 'pl': return 'pl-PL';
                        case 'en': return 'en-US';
                        case 'de': return 'de-DE';
                        case 'uk': return 'uk-UA';
                        default: return 'pl-PL';
                      }
                    };
                    
                    const formatDate = (dateStr) => {
                      if (!dateStr) return '-';
                      const date = new Date(dateStr);
                      if (isNaN(date.getTime())) return '-';
                      return date.toLocaleDateString(getLocale());
                    };
                    
                    // Check if needs activation based on new API structure
                    // pending passes have status: 'pending_activation'
                    const isPending = pass.status === 'pending_activation' || pass.activationStatus === 'pending_activation';
                    const isActive = pass.isActive || pass.status === 'active' || pass.activationStatus === 'active';
                    
                    return (
                    <div
                      key={pass.purchase_id || pass.id || pass.fitnetKlientKarnetId}
                      className="p-4 bg-black/5 dark:bg-white/5 rounded-xl"
                      data-testid={`pass-${pass.purchase_id || pass.id || pass.fitnetKlientKarnetId}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-lg text-foreground">{pass.passName || pass.name}</p>
                          {/* Show UID for online purchases (not for passes added at reception) */}
                          {(pass.pass_code || pass.passCode) && (
                            <p className="text-muted-foreground text-sm">UID: <span className="font-mono">{pass.pass_code || pass.passCode}</span></p>
                          )}
                          {/* Source badge - Online vs In-person */}
                          <div className="flex gap-2 mt-1">
                            {(pass.pass_code || pass.passCode) ? (
                              <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-400">
                                {t('passes.online', 'Online')}
                              </span>
                            ) : (
                              <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-400">
                                {t('passes.inPerson', 'In-person')}
                              </span>
                            )}
                            {pass.isExpired && (
                              <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-400">
                                {t('passes.expired', 'Expired')}
                              </span>
                            )}
                          </div>
                          {/* Show entries only for quantity-based passes */}
                          {pass.passType === 'quantity' && pass.entriesRemaining !== null && pass.entriesRemaining !== undefined ? (
                            <p className="text-sm mt-1">
                              {t('passes.remainingEntries', 'Remaining entries:')} <span className="font-bold">{pass.entriesRemaining}</span>
                              {pass.entriesTotal && (
                                <span className="text-muted-foreground"> / {pass.entriesTotal}</span>
                              )}
                            </p>
                          ) : pass.entries !== null && pass.entries !== undefined && pass.entries > 0 && (
                            <p className="text-sm mt-1">
                              {t('passes.entries', 'Entries:')} <span className="font-bold">{pass.entries}</span>
                            </p>
                          )}
                          {/* Show assigned person */}
                          <p className="text-sm text-primary mt-2 flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {pass.ownerName || pass.forUserName || getPersonName(pass.for_user_id || user?.user_id)}
                          </p>
                        </div>
                        <div className="text-right">
                          {/* Status display */}
                          {isPending ? (
                            <div className="mb-2">
                              <p className="text-sm font-medium text-amber-500 flex items-center gap-1 justify-end">
                                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                {t('passes.pendingActivation', 'Pending activation')}
                              </p>
                              {/* Activate button for pending passes */}
                              <button
                                onClick={() => handleActivateMyPass(pass.purchase_id)}
                                disabled={activatingPassId === pass.purchase_id}
                                className="mt-2 px-3 py-1.5 text-xs font-medium bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1 ml-auto"
                                data-testid={`activate-pass-btn-${pass.purchase_id}`}
                              >
                                {activatingPassId === pass.purchase_id ? (
                                  <>
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    {t('common.activating', 'Activating...')}
                                  </>
                                ) : (
                                  <>
                                    <Check className="w-3 h-3" />
                                    {t('passes.activateNow', 'Activate now')}
                                  </>
                                )}
                              </button>
                            </div>
                          ) : isActive ? (
                            <p className="text-sm font-medium text-green-500 flex items-center gap-1 justify-end">
                              <span className="w-2 h-2 rounded-full bg-green-500" />
                              {t('account.active', 'Active')}
                            </p>
                          ) : (
                            <p className="text-sm font-medium text-gray-400">
                              {t('account.inactive', 'Inactive')}
                            </p>
                          )}
                          
                          {/* Show validity dates or activation countdown */}
                          {(() => {
                            // Check if dates are placeholders (year 0001 or very old)
                            const isPlaceholderDate = (dateStr) => {
                              if (!dateStr) return true;
                              return dateStr.startsWith('0001') || dateStr.startsWith('1.01.1') || dateStr.startsWith('1-01-1');
                            };
                            
                            const hasRealEndDate = expiryDateStr && !isPlaceholderDate(expiryDateStr);
                            const hasRealStartDate = validFromStr && !isPlaceholderDate(validFromStr);
                            
                            if (isActive) {
                              // Active pass - show dates if available
                              return (
                                <>
                                  {hasRealEndDate && (
                                    <p className="text-sm text-muted-foreground">
                                      {t('passes.validUntil', 'Valid until:')} {formatDate(expiryDateStr)}
                                    </p>
                                  )}
                                  {hasRealStartDate && (
                                    <p className="text-xs text-muted-foreground">
                                      {t('passes.validFrom', 'from:')} {formatDate(validFromStr)}
                                    </p>
                                  )}
                                </>
                              );
                            } else if (isPending) {
                              // Pass pending activation - show countdown to activate, NOT validity dates
                              // For periodic passes (30-day, etc.) the validity starts from activation
                              const purchaseDate = new Date(pass.paidAt || pass.createdAt || pass.fitnetAssignedAt || pass.purchasedAt || new Date());
                              if (isNaN(purchaseDate.getTime())) {
                                return (
                                  <p className="text-xs text-muted-foreground">
                                    {t('passes.validFromActivation', 'Valid from activation')}
                                  </p>
                                );
                              }
                              const deadlineDate = new Date(purchaseDate);
                              deadlineDate.setDate(deadlineDate.getDate() + activationDeadlineDays);
                              const today = new Date();
                              const daysRemaining = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
                              
                              return (
                                <>
                                  <p className="text-xs text-muted-foreground">
                                    {t('passes.validFromActivation', 'Valid from activation')}
                                  </p>
                                  {daysRemaining > 0 ? (
                                    <p className="text-xs text-amber-400 mt-1">
                                      {t('passes.daysToActivate', '{days} days to activate').replace('{days}', daysRemaining)}
                                    </p>
                                  ) : (
                                    <p className="text-xs text-red-500 mt-1">
                                      {t('passes.activationDeadlinePassed', 'Activation deadline passed!')}
                                    </p>
                                  )}
                                </>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    </div>
                  )})
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Cancel Booking Modal */}
      <AlertDialog open={cancelModal.open} onOpenChange={(open) => !open && setCancelModal({ open: false, bookingId: null, isPaid: false })}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {t('booking.cancelConfirmTitle', 'Cancel booking?')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {cancelModal.isPaid 
                ? t('booking.cancelConfirmPaid', 'Are you sure you want to cancel this booking? An automatic refund will be initiated to your account.')
                : t('booking.cancelConfirmUnpaid', 'Are you sure you want to cancel this booking? This action cannot be undone.')
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              {t('common.noKeepIt', 'No, keep it')}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmCancelBooking}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {t('common.yesCancel', 'Yes, cancel')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Result Modal */}
      <AlertDialog open={cancelResult.show} onOpenChange={(open) => !open && setCancelResult({ show: false, success: false, message: '' })}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className={cancelResult.success ? "text-green-500" : "text-red-500"}>
              {cancelResult.success 
                ? `✓ ${t('common.success', 'Success')}`
                : `✗ ${t('common.error', 'Error')}`
              }
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-300">
              {cancelResult.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={() => setCancelResult({ show: false, success: false, message: '' })}
              className="bg-primary text-white hover:bg-primary/90"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AccountPage;
